import { Socket } from 'socket.io-client';
import {
  GameDataFromServer,
  SocketCallback,
} from '../../components/room/types';
import { CreateGameConfig } from '@par/shared/types/game';
import { Profile } from '@par/shared/types/player';
import { Mode } from '@par/shared/common/types';

export const createGame = async (
  socket: Socket,
  profile: Profile,
  createGameSuccess: (gameData: GameDataFromServer) => void,
  requestFailed: (message?: string) => void,
  isDaily: boolean,
  createGameConfig: CreateGameConfig,
  fromSoloPunchcard?: boolean,
  fromMain?: boolean,
  fromStarterKit?: boolean
) => {
  socket.emit('canCreateGame', profile.id, (res: SocketCallback) => {
    if (res.status === 'ok') {
      if (isDaily) {
        handleCreateDailyGame(
          socket,
          profile,
          createGameConfig,
          createGameSuccess,
          requestFailed
        );
        return;
      }
      if (
        profile.seedMatch > 0 &&
        !createGameConfig.voluntarySeedMatch &&
        createGameConfig.matchMaking
      ) {
        handleSearchMatchMakingGame(
          socket,
          profile,
          createGameConfig,
          createGameSuccess,
          requestFailed
        );
        return;
      }
      handleCreateGame(
        socket,
        profile,
        createGameConfig,
        createGameSuccess,
        requestFailed,
        fromSoloPunchcard,
        fromMain,
        fromStarterKit
      );
    }
    if (res.status === 'failed') requestFailed(res.message);
  });
};

const handleCreateGame = (
  socket: Socket,
  profile: Profile,
  createGameConfig: CreateGameConfig,
  createGameSuccess: (
    gameData: GameDataFromServer,
    fromSoloPunchcard?: boolean,
    fromMain?: boolean,
    fromStarterKit?: boolean
  ) => void,
  requestFailed: (message: string) => void,
  fromSoloPunchcard?: boolean,
  fromMain?: boolean,
  fromStarterKit?: boolean
) => {
  const {
    allowSpectators,
    autoStartDuration,
    categorySelections,
    categorySet,
    createEmptyGame,
    customGameName,
    customSitAndGoMaxPlayers,
    daily,
    dailySet,
    demo,
    demons,
    flashcardDeck,
    flashcardDeckId,
    gameType,
    ghostChallenge,
    ghostChallengeFrom,
    ghostChallengeTo,
    joinGameTimerDuration,
    league,
    matchMaking,
    missedDaily,
    mode,
    ranked,
    starterKit,
    survivalMode,
    teamUsernames,
    testTournamentQuestionsSet,
    tournament,
    tournamentCutoff,
    tournamentCutoffType,
    tournamentQuestionCount,
    tournamentRound,
    variant,
    voluntarySeedMatch,
    whoCanJoin,
  } = createGameConfig;
  socket.emit(
    createEmptyGame ? 'createEmptyGame' : 'createGameAndJoin',
    {
      allowSpectators,
      autoStartDuration,
      categorySelections,
      categorySet: categorySet,
      customGameName: customGameName,
      customSitAndGoMaxPlayers,
      daily,
      dailySet,
      demo,
      demons,
      enableCustomQuestionsInSolos:
        profile.settings?.enableCustomQuestionsInSolos,
      fastNext: profile.settings?.fastNext,
      flashcardDeck,
      flashcardDeckId,
      gameType: gameType,
      ghostChallenge,
      ghostChallengeFrom,
      ghostChallengeTo,
      joinGameTimerDuration,
      league,
      matchMaking,
      missedDaily,
      mode,
      profileId: profile.id,
      ranked,
      starterKit,
      survivalMode,
      teamUsernames,
      testTournamentQuestionsSet,
      tournament,
      tournamentCutoff,
      tournamentCutoffType,
      tournamentQuestionCount,
      tournamentRound,
      variant,
      voluntarySeedMatch,
      whoCanJoin,
    },
    (res: SocketCallback) => {
      if (res.status === 'ok') {
        const gameData: GameDataFromServer = res.data;
        createGameSuccess(
          gameData,
          fromSoloPunchcard,
          fromMain,
          fromStarterKit
        );
      }
      if (res.status === 'failed') {
        requestFailed(res.message || '');
      }
    }
  );
};

const handleCreateDailyGame = (
  socket: Socket,
  profile: Profile,
  createGameConfig: CreateGameConfig,
  createGameSuccess: (gameData: GameDataFromServer) => void,
  requestFailed: (message: string) => void
) => {
  const { dailySet, missedDaily } = createGameConfig;
  socket.emit(
    'createDailyGame',
    {
      profileId: profile.id,
      dailySet,
      missedDaily,
      fastNext: profile.settings?.fastNext,
    },
    (res: SocketCallback) => {
      if (res.status === 'ok') {
        const gameData: GameDataFromServer = res.data;
        createGameSuccess(gameData);
      }
      if (res.status === 'failed') {
        requestFailed(res.message || '');
      }
    }
  );
};

const handleSearchMatchMakingGame = async (
  socket: Socket,
  profile: Profile,
  createGameConfig: CreateGameConfig,
  createGameSuccess: (gameData: GameDataFromServer) => void,
  requestFailed: (message: string) => void
) => {
  const { expandedSearchRange, searchOptions } = createGameConfig;
  socket.emit(
    'searchMatchMakingGame',
    {
      fastNext: profile.settings?.fastNext,
      profileId: profile.id,
      expandedSearchRange: expandedSearchRange,
      withHandicap: searchOptions === 'handicap',
    },
    (res: SocketCallback) => {
      if (res.status === 'ok') {
        const gameData: GameDataFromServer = res.data;
        createGameSuccess(gameData);
      }
      if (res.status === 'failed') {
        requestFailed(res.message || '');
      }
    }
  );
};

export const rejoinGame = async (
  socket: Socket,
  profile: Profile,
  rejoinGameSuccess: (gameData: GameDataFromServer) => void,
  requestFailed: (message: string) => void
) => {
  socket.emit('canRejoinGame', { profileId: profile.id }, (res: SocketCallback) => {
    if (res.status === 'ok') {
      const gameData: GameDataFromServer = res.data;
      rejoinGameSuccess(gameData);
    }
    if (res.status === 'failed') {
      requestFailed(res.message || '');
    }
  });
};

export const spectateGame = async (
  socket: Socket,
  profile: Profile,
  gameId: string,
  spectateGameSuccess: (gameData: GameDataFromServer) => void,
  requestFailed: (message: string) => void
) => {
  socket.emit(
    'canSpectateGame',
    profile.id,
    gameId,
    (res: SocketCallback) => {
      if (res.status === 'ok') {
        const gameData: GameDataFromServer = res.data;
        spectateGameSuccess(gameData);
      }
      if (res.status === 'failed') {
        requestFailed(res.message || '');
      }
    }
  );
};

export const joinGame = (
  socket: Socket,
  profile: Profile,
  gameId: string,
  mode: Mode,
  joinGameSuccess: (gameData: GameDataFromServer) => void,
  requestFailed: (message?: string) => void
) => {
  if (!gameId) return;
  if (mode === 'teams') {
    socket.emit(
      'joinTeamGame',
      { gameId: gameId, profileId: profile.id },
      (res: SocketCallback) => {
        if (res.status === 'ok') {
          const gameData: GameDataFromServer = res.data;
          joinGameSuccess(gameData);
        }
        if (res.status === 'failed') {
          requestFailed(res.message || '');
        }
      }
    );
  } else {
    socket.emit(
      'joinGame',
      { gameId: gameId, profileId: profile.id },
      (res: SocketCallback) => {
        if (res.status === 'ok') {
          const gameData: GameDataFromServer = res.data;
          joinGameSuccess(gameData);
        }
        if (res.status === 'failed') {
          requestFailed(res.message || '');
        }
      }
    );
  }
};

export const joinLeagueGame = (
  socket: Socket,
  profile: Profile,
  gameId: string,
  mode: Mode,
  joinGameSuccess: (gameData: GameDataFromServer) => void,
  requestFailed: (message?: string) => void
) => {
  if (!gameId) return;
  socket.emit(
    'joinLeagueGame',
    { gameId: gameId, profileId: profile.id },
    (res: SocketCallback) => {
      if (res.status === 'ok') {
        const gameData: GameDataFromServer = res.data;
        joinGameSuccess(gameData);
      }
      if (res.status === 'failed') {
        requestFailed(res.message || '');
      }
    }
  );
};

export const playGhostChallenge = (
  socket: Socket,
  profile: Profile,
  gameId: string,
  ghostChallengeFrom: string,
  ghostChallengeTo: string,
  createGameSuccess: (gameData: GameDataFromServer) => void,
  requestFailed: (message?: string) => void
) => {
  if (!gameId) return;
  socket.emit(
    'playGhostChallenge',
    {
      fastNext: profile.settings?.fastNext,
      gameId: gameId,
      ghostChallengeFrom,
      ghostChallengeTo,
      profileId: profile.id,
    },
    (res: SocketCallback) => {
      if (res.status === 'ok') {
        const gameData: GameDataFromServer = res.data;
        createGameSuccess(gameData);
      }
      if (res.status === 'failed') {
        requestFailed(res.message || '');
      }
    }
  );
};
